import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import ThemeToggle from "./ThemeToggle";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function Downloads() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgEdits, setOrgEdits] = useState({});

  const token = localStorage.getItem("qre_token");

  const loadFiles = () => {
    fetch(`${API}/admin/csv-list`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => {
        if (r.status === 403) { navigate("/scanner"); return null; }
        if (!r.ok) throw new Error("Error");
        return r.json();
      })
      .then(data => { if (data) setFiles(data.files); })
      .catch(() => setError("Error al cargar archivos"))
      .finally(() => setLoading(false));
  };

  const loadMetrics = () => {
    fetch(`${API}/admin/metrics`, { headers: { "Authorization": `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setMetrics(data); })
      .catch(() => {});
  };

  const loadOrgs = () => {
    fetch(`${API}/admin/organizations`, { headers: { "Authorization": `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setOrgs(data.organizations); })
      .catch(() => {});
  };

  const handleSaveOrg = (domain) => {
    const edit = orgEdits[domain];
    if (!edit) return;
    fetch(`${API}/admin/organizations/${domain}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({
        plan: edit.plan,
        monthly_scan_limit: edit.unlimited ? null : parseInt(edit.monthly_scan_limit, 10) || 0
      })
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => loadOrgs())
      .catch(() => alert("Error al actualizar la organización"));
  };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    loadFiles();
    loadMetrics();
    loadOrgs();
  }, []);

  const handleDownload = (filename, format) => {
    const endpoint = format === 'xlsx' 
      ? `${API}/admin/xlsx-download/${filename}`
      : `${API}/admin/csv-download/${filename}`;
    const downloadName = format === 'xlsx' ? filename.replace(".csv", ".xlsx") : filename;

    fetch(endpoint, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error("Error");
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = downloadName;
        a.click();
        URL.revokeObjectURL(a.href);
        setFiles(prev => prev.map(f => f.filename === filename ? { ...f, downloaded: true } : f));
      })
      .catch(() => alert("Error al descargar"));
  };

  const handleDeleteClick = (filename, downloaded) => {
    if (!downloaded) {
      alert("Este archivo no ha sido descargado aún.");
      return;
    }
    setDeleteTarget(filename);
    setDeleteConfirmed(false);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmed) {
      setDeleteConfirmed(true);
      return;
    }
    fetch(`${API}/admin/csv-delete/${deleteTarget}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error("Error");
        setFiles(prev => prev.filter(f => f.filename !== deleteTarget));
        setDeleteTarget(null);
        setDeleteConfirmed(false);
      })
      .catch(() => alert("Error al eliminar"));
  };

  const filtered = files.filter(f =>
    f.filename.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="scanner-container">
      <div className="scanner-header">
        <div className="flex items-center justify-between gap-2.5">
          <h1>Archivos CSV</h1>
          <ThemeToggle className="text-white hover:bg-white/20 hover:text-white shrink-0" />
        </div>
        <div className="user-info">
          <button className="logout-button" onClick={() => navigate("/scanner")} data-testid="back-to-scanner">
            Volver
          </button>
        </div>
      </div>

      <div className="p-4">
        {metrics && (
          <div data-testid="metrics-panel" className="bg-card rounded-lg p-4 mb-4 border border-border">
            <div className="font-bold text-primary mb-3">Métricas de uso</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="text-center p-2 bg-background rounded-md">
                <div className="text-[22px] font-bold text-primary">{metrics.scans_today}</div>
                <div className="text-[11px] text-muted-foreground">Hoy</div>
              </div>
              <div className="text-center p-2 bg-background rounded-md">
                <div className="text-[22px] font-bold text-primary">{metrics.scans_last_7_days}</div>
                <div className="text-[11px] text-muted-foreground">Últimos 7 días</div>
              </div>
              <div className="text-center p-2 bg-background rounded-md">
                <div className="text-[22px] font-bold text-primary">{metrics.scans_this_month}</div>
                <div className="text-[11px] text-muted-foreground">Este mes</div>
              </div>
              <div className="text-center p-2 bg-background rounded-md">
                <div className="text-[22px] font-bold text-primary">{metrics.active_users_last_30_days}</div>
                <div className="text-[11px] text-muted-foreground">Usuarios activos (30d)</div>
              </div>
            </div>
            {metrics.daily_series_last_30_days.length > 0 && (
              <div className="w-full h-[140px]">
                <ResponsiveContainer>
                  <BarChart data={metrics.daily_series_last_30_days}>
                    <XAxis dataKey="day" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {orgs.length > 0 && (
          <div data-testid="organizations-panel" className="bg-card rounded-lg p-4 mb-4 border border-border">
            <div className="font-bold text-primary mb-3">Organizaciones y planes</div>
            {orgs.map((org) => {
              const edit = orgEdits[org.domain] || {
                plan: org.plan,
                monthly_scan_limit: org.monthly_scan_limit ?? "",
                unlimited: org.monthly_scan_limit === null
              };
              return (
                <div key={org.domain} data-testid={`org-${org.domain}`} className="p-2.5 mb-2 rounded-md border border-border">
                  <div className="flex justify-between text-[13px] mb-1.5">
                    <strong>{org.domain}</strong>
                    <span className="text-muted-foreground">{org.scans_this_month} escaneos este mes</span>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <select
                      value={edit.plan}
                      onChange={(e) => setOrgEdits(prev => ({ ...prev, [org.domain]: { ...edit, plan: e.target.value } }))}
                      className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                    >
                      <option value="free">free</option>
                      <option value="pro">pro</option>
                    </select>
                    <label className="text-xs flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={edit.unlimited}
                        onChange={(e) => setOrgEdits(prev => ({ ...prev, [org.domain]: { ...edit, unlimited: e.target.checked } }))}
                        className="accent-primary"
                      />
                      Ilimitado
                    </label>
                    {!edit.unlimited && (
                      <Input
                        type="number" min="0"
                        value={edit.monthly_scan_limit}
                        onChange={(e) => setOrgEdits(prev => ({ ...prev, [org.domain]: { ...edit, monthly_scan_limit: e.target.value } }))}
                        placeholder="Límite mensual"
                        className="h-8 text-xs w-[110px]"
                      />
                    )}
                    <Button variant="brand" size="sm" onClick={() => handleSaveOrg(org.domain)}>
                      Guardar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Input
          type="text"
          className="h-12 text-base border-2 mb-3"
          placeholder="Buscar archivo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-input"
        />

        {loading && <div className="text-muted-foreground text-base">Cargando archivos...</div>}
        {error && <div className="text-destructive text-sm">{error}</div>}

        {!loading && filtered.length === 0 && (
          <p className="text-center text-muted-foreground mt-5">
            {search ? "Sin resultados" : "No hay archivos CSV disponibles"}
          </p>
        )}

        {filtered.map((f) => (
          <div
            key={f.filename}
            data-testid={`csv-file-${f.filename}`}
            className={`p-3 mb-2 bg-card rounded-lg border ${f.downloaded ? 'border-muted-foreground' : 'border-border'}`}
          >
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-primary text-[13px] break-all">
                  {f.filename}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {f.size_kb} KB &middot; {f.modified}
                  {f.downloaded && <span className="text-primary font-bold"> &middot; Descargado</span>}
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0 ml-2">
                <Button
                  size="sm"
                  className="bg-green-700 text-white hover:bg-green-800 h-auto px-2.5 py-2 text-[11px]"
                  onClick={() => handleDownload(f.filename, 'xlsx')}
                  data-testid={`download-xlsx-${f.filename}`}
                >
                  XLSX
                </Button>
                <Button
                  variant="brand"
                  size="sm"
                  className="h-auto px-2.5 py-2 text-[11px]"
                  onClick={() => handleDownload(f.filename, 'csv')}
                  data-testid={`download-csv-${f.filename}`}
                >
                  CSV
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-auto px-2.5 py-2 text-[11px] disabled:opacity-40"
                  disabled={!f.downloaded}
                  onClick={() => handleDeleteClick(f.filename, f.downloaded)}
                  data-testid={`delete-${f.filename}`}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmed(false); } }}>
        <AlertDialogContent className="max-w-[320px] text-center" data-testid="delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">
              {deleteConfirmed ? "¿Estás seguro?" : "Eliminar archivo"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center break-all">
              {deleteConfirmed ? "Esta acción no se puede deshacer." : deleteTarget}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center">
            <AlertDialogCancel data-testid="delete-cancel">Cancelar</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDeleteConfirm} data-testid="delete-confirm">
              {deleteConfirmed ? "Sí, eliminar" : "Confirmar"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Downloads;
